import { Box, FormControl, FormHelperText, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface Props {
    label: string;
    error?: boolean;
    helperText?: string;
    disabled?: boolean;
    maxLength?: number;
    value: string;
    onChange: (value: string) => void;
}

const EMPTY_QUILL_VALUE = '<p><br></p>';

const TemplateInput = ({
    disabled,
    maxLength,
    value,
    label,
    error,
    helperText,
    onChange,
}: Props) => {
    const quillRef = useRef<ReactQuill | null>(null);
    const [currentCharacterCount, setCurrentCharacterCount] = useState<number>(0);

    useEffect(() => {
        if (quillRef.current) {
            const quill = quillRef.current.getEditor();
            setCurrentCharacterCount(Math.max(quill.getLength() - 1, 0));
        }
    }, [value]);

    const handleChange = (nextValue: string) => {
        if (quillRef.current) {
            const quill = quillRef.current.getEditor();
            const textLength = quill.getLength() - 1;
            const normalizedValue = textLength === 0 ? '' : nextValue;

            if (maxLength) {
                // there is always one newline character in quill so we have to deduct 1
                if (textLength <= maxLength) {
                    onChange(normalizedValue);
                    setCurrentCharacterCount(textLength);
                } else {
                    quill.deleteText(maxLength, textLength - maxLength);
                }
            } else {
                onChange(normalizedValue);
                setCurrentCharacterCount(textLength);
            }
        }
    };

    return (
        <FormControl error={error} fullWidth disabled={disabled} variant="standard">
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 0.75,
                    gap: 1,
                }}
            >
                <Typography
                    variant="body2"
                    component="label"
                    sx={{
                        color: error ? 'error.main' : 'text.primary',
                        fontWeight: 500,
                    }}
                >
                    {label}
                </Typography>
                {maxLength && (
                    <Typography
                        component="span"
                        variant="caption"
                        sx={{
                            color: error ? 'error.main' : 'text.secondary',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {`${currentCharacterCount} / ${maxLength}`}
                    </Typography>
                )}
            </Box>

            <Box
                sx={{
                    border: '1px solid',
                    borderColor: error ? 'error.main' : 'rgba(0, 0, 0, 0.23)',
                    borderRadius: 1,
                    overflow: 'hidden',
                    backgroundColor: disabled ? 'action.disabledBackground' : 'background.paper',
                    transition: (theme) =>
                        theme.transitions.create(['border-color', 'box-shadow'], {
                            duration: theme.transitions.duration.shorter,
                        }),
                    '&:hover': {
                        borderColor: error ? 'error.main' : 'text.primary',
                    },
                    '&:focus-within': {
                        borderColor: error ? 'error.main' : 'primary.main',
                        boxShadow: (theme) =>
                            error
                                ? `0 0 0 1px ${theme.palette.error.main}`
                                : `0 0 0 1px ${theme.palette.primary.main}`,
                    },
                    '& .ql-toolbar.ql-snow': {
                        border: 'none',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: disabled ? 'action.hover' : 'grey.50',
                    },
                    '& .ql-container.ql-snow': {
                        border: 'none',
                    },
                    '& .ql-editor': {
                        height: 140,
                        overflowY: 'auto',
                        fontFamily: 'inherit',
                        fontSize: '1rem',
                        lineHeight: 1.5,
                        color: 'text.primary',
                    },
                    '& .ql-editor.ql-blank::before': {
                        fontStyle: 'normal',
                        color: 'text.disabled',
                    },
                }}
            >
                <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={value || EMPTY_QUILL_VALUE}
                    readOnly={disabled}
                    onChange={handleChange}
                />
            </Box>

            <FormHelperText sx={{ mx: 0, mt: 0.75 }}>{helperText || ' '}</FormHelperText>
        </FormControl>
    );
};

export default TemplateInput;
